// =============================================================================
// selectors/confluenceEdge.ts — Confluence Edge, re-exported for ui/.
// -----------------------------------------------------------------------------
// ⚠ `ui/` may not import `domain/` AT ALL, not even a type (the layering guard
// enforces it).
//
// ⚠ `resolveMarks` lives HERE rather than in the tab because it is the join
// between two domain modules — the registry parser and the edge maths — and a
// component that assembles it itself is a component that will one day forget to
// exclude category parents. That single omission changes every bucket on the
// card and looks entirely plausible.
// =============================================================================

import {
  activeConfluenceIds, confluenceRegistry, isConfluenceCategory,
  type ConfluenceNode,
} from '../domain/confluences';
import { marksOf, type ResolvedMark } from '../domain/confluenceEdge';

export {
  confluenceEdge, confluenceBadge, confluenceHeatBand, confluenceMetricValue,
  confluencePairGrid, confluenceRedundant, patternConfluenceGrid,
  sortConfluenceRanking, stackingRead, winVerdict, marksOf,
  CONFLUENCE_METRICS, CONFLUENCE_MIN_SAMPLE, CONFLUENCE_MYDATA_THRESHOLD,
  CONFLUENCE_ROBUST_SAMPLE, CONFLUENCE_CELL_MIN, WIN_DEFINITIONS,
  CONFLUENCE_PREVIEW, CONFLUENCE_PREVIEW_PAIR, CONFLUENCE_PREVIEW_PATTERN,
} from '../domain/confluenceEdge';

export type {
  ConfluenceBadge, ConfluenceCell, ConfluenceEdge, ConfluenceGrid,
  ConfluenceMetric, ConfluenceRankRow, ConfluenceTrade, ResolvedMark,
  ScoredStats, StackingBucket, StackingRead, WinDefinition, WinVerdict,
} from '../domain/confluenceEdge';

/**
 * Build the "what did this trade confirm with" reader from the stored registry.
 *
 * ⚠ THREE RULES, ALL EASY TO DROP, ALL PROVEN NECESSARY ON JOSH'S OWN BOOK:
 *   • category parents are excluded — counting a heading beside its own children
 *     double-counts one observation, and it is what makes a naive recomputation
 *     disagree with the old client's buckets;
 *   • an id the registry no longer holds is kept and FLAGGED, not dropped —
 *     `volume-profile` and `volpro-lvn` are real rows on his card;
 *   • the stored shape may be an object of `{active:true}` OR a bare `true`, and
 *     both appear in this database.
 */
export function confluenceMarkReader(
  storedRegistry: unknown,
): (stored: unknown) => ResolvedMark[] {
  const registry: readonly ConfluenceNode[] = confluenceRegistry(storedRegistry);
  const names = new Map<string, string>();
  for (const n of registry) {
    names.set(n.id, n.label);
    for (const c of n.children) names.set(c.id, c.label);
  }
  const resolver = {
    nameOf: (id: string) => names.get(id) ?? null,
    isCategory: (id: string) => isConfluenceCategory(registry, id),
  };
  return (stored: unknown) => marksOf([...activeConfluenceIds(stored)], resolver);
}
